import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import CTASection from '../components/CTASection';
import useSEO from '../hooks/useSEO';
import { CASE_STUDIES, CASE_STUDY_CATEGORIES } from '../caseStudies';

const CaseStudies = () => {
  const [filter, setFilter] = useState('all');
  useSEO({
    title: 'Case Studies, Marketing Agency Liverpool | Yellow',
    description: 'Real results for real businesses. Explore how Yellow, a marketing agency Liverpool, has transformed communications, recruitment and training campaigns for household-name clients across the UK.',
    path: '/case-studies'
  });

  const filtered = useMemo(
    () => (filter === 'all' ? CASE_STUDIES : CASE_STUDIES.filter((c) => c.category === filter)),
    [filter]
  );

  return (
    <>
      <section className="pt-36 pb-14 lg:pt-44">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <p className="section-label mb-6">Our Work</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.02] max-w-5xl">
            Real results through <span className="text-[#F5CD1B]">innovation and strategy.</span>
          </h1>
          <p className="text-white/70 text-xl leading-relaxed mt-8 max-w-3xl">
            Explore how we've transformed communications across industries with strategic thinking and creative execution.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-8">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-2" data-testid="case-studies-filters">
            {CASE_STUDY_CATEGORIES.map((cat) => {
              const active = filter === cat.key;
              return (
                <button
                  key={cat.key}
                  onClick={() => setFilter(cat.key)}
                  data-testid={`case-filter-${cat.key}`}
                  className={`text-sm font-medium px-4 py-2.5 rounded-full border transition-all ${
                    active
                      ? 'bg-[#F5CD1B] text-[#0b0b0d] border-[#F5CD1B]'
                      : 'bg-transparent text-white/80 border-white/15 hover:border-[#F5CD1B]/60 hover:text-white'
                  }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-10 lg:py-14">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="case-studies-grid">
            {filtered.map((cs) => (
              <Link
                to={`/case-studies/${cs.slug}`}
                key={cs.slug}
                data-testid={`case-card-${cs.slug}`}
                className="group relative overflow-hidden rounded-2xl bg-[#111114] border border-white/5 hover:border-[#F5CD1B]/30 transition-colors flex flex-col"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={cs.hero}
                    alt={`${cs.title}, case study by Yellow, marketing agency Liverpool`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0d]/70 via-transparent to-transparent"></div>
                  <span className="absolute top-4 left-4 text-[10px] uppercase tracking-widest text-[#F5CD1B] bg-[#0b0b0d]/70 px-2 py-1 rounded-md border border-[#F5CD1B]/20">
                    {cs.categoryLabel}
                  </span>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-2">
                    {cs.client} · {cs.sector}
                  </p>
                  <h3 className="font-display text-xl md:text-2xl font-bold text-white leading-snug mb-3 group-hover:text-[#F5CD1B] transition-colors">
                    {cs.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed line-clamp-3 flex-1">
                    {cs.summary}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {cs.services.slice(0, 3).map((s) => (
                      <span key={s} className="text-[11px] text-white/50 bg-white/[0.04] border border-white/5 px-2.5 py-1 rounded-full">
                        {s}
                      </span>
                    ))}
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 text-[#F5CD1B] text-sm font-semibold">
                    View case study
                    <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-white/50 text-center py-16">No case studies in this category yet.</p>
          )}
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default CaseStudies;
